import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Plus, Trash2, X, Search, FileText, Euro, CheckCircle, Upload, FileUp, Package } from 'lucide-react';

function ComparisonMapDetail({ map: initialMap, onBack, suppliers, budgets }) {
  const [map, setMap] = useState(initialMap);
  const [itemDesc, setItemDesc] = useState('');
  const [itemUnit, setItemUnit] = useState('un');
  const [itemQty, setItemQty] = useState(1);
  const [showImportBudget, setShowImportBudget] = useState(false);
  const [showImportPdf, setShowImportPdf] = useState(false);
  const [selectedSupplierForPdf, setSelectedSupplierForPdf] = useState('');
  const [pdfItems, setPdfItems] = useState([]);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const refresh = () => { api.get(`/comparison-maps`).then(r => { const m = r.data.find(x => x.id === map.id); if (m) setMap(m); }); };

  const addItem = async () => {
    if (!itemDesc) return;
    const items = [...(map.items || []), { id: String(Date.now()), description: itemDesc, unit: itemUnit, quantity: itemQty }];
    await api.put(`/comparison-maps/${map.id}`, { items });
    setItemDesc(''); setItemQty(1); refresh();
  };

  const removeItem = async (itemId) => {
    const items = (map.items || []).filter(i => i.id !== itemId);
    await api.put(`/comparison-maps/${map.id}`, { items });
    refresh();
  };

  const addQuote = async (supplierId, itemId, price) => {
    const quotes = [...(map.quotes || [])];
    const idx = quotes.findIndex(q => q.supplier_id === supplierId && q.item_id === itemId);
    if (idx >= 0) quotes[idx].price = price;
    else quotes.push({ supplier_id: supplierId, item_id: itemId, price });
    await api.put(`/comparison-maps/${map.id}`, { quotes });
    refresh();
  };

  const getQuote = (supplierId, itemId) => (map.quotes || []).find(q => q.supplier_id === supplierId && q.item_id === itemId)?.price;

  const addSupplierToMap = async (sid) => {
    if ((map.suppliers || []).includes(sid)) return;
    await api.put(`/comparison-maps/${map.id}`, { suppliers: [...(map.suppliers || []), sid] });
    refresh();
  };

  const removeSupplierFromMap = async (sid) => {
    await api.put(`/comparison-maps/${map.id}`, { suppliers: (map.suppliers || []).filter(s => s !== sid), quotes: (map.quotes || []).filter(q => q.supplier_id !== sid) });
    refresh();
  };

  // Import items from budget
  const importFromBudget = async (budgetId) => {
    const budget = budgets.find(b => b.id === budgetId);
    if (!budget || !budget.items?.length) return;
    
    const newItems = budget.items.map(item => ({
      id: String(Date.now()) + '_' + Math.random().toString(36).substr(2, 9),
      description: item.descricao || '',
      unit: item.unidade || 'un',
      quantity: item.quantidade || 1
    })).filter(i => i.description);
    
    const items = [...(map.items || []), ...newItems];
    await api.put(`/comparison-maps/${map.id}`, { items });
    setShowImportBudget(false);
    refresh();
  };

  // Handle PDF upload for supplier quotation
  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSupplierForPdf) return;
    
    setUploadingPdf(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        // Call backend to extract items from PDF
        const { data } = await api.post('/comparison-maps/extract-pdf', { 
          pdf_base64: base64,
          supplier_id: selectedSupplierForPdf
        });
        if (data.items?.length > 0) {
          setPdfItems(data.items);
        } else {
          alert('Nao foi possivel extrair itens do PDF. Tente adicionar manualmente.');
        }
        setUploadingPdf(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error(err);
      alert('Erro ao processar PDF');
      setUploadingPdf(false);
    }
  };

  // Apply PDF prices to matching items
  const applyPdfPrices = async () => {
    if (!pdfItems.length || !selectedSupplierForPdf) return;
    
    const quotes = [...(map.quotes || [])];
    
    pdfItems.forEach(pdfItem => {
      // Find matching item by description (fuzzy match)
      const mapItem = (map.items || []).find(i => 
        i.description.toLowerCase().includes(pdfItem.description.toLowerCase()) ||
        pdfItem.description.toLowerCase().includes(i.description.toLowerCase())
      );
      
      if (mapItem && pdfItem.price) {
        const idx = quotes.findIndex(q => q.supplier_id === selectedSupplierForPdf && q.item_id === mapItem.id);
        if (idx >= 0) quotes[idx].price = pdfItem.price;
        else quotes.push({ supplier_id: selectedSupplierForPdf, item_id: mapItem.id, price: pdfItem.price });
      }
    });
    
    await api.put(`/comparison-maps/${map.id}`, { quotes });
    setPdfItems([]);
    setShowImportPdf(false);
    setSelectedSupplierForPdf('');
    refresh();
  };

  const mapSuppliers = suppliers.filter(s => (map.suppliers || []).includes(s.id));
  const availableSuppliers = suppliers.filter(s => !(map.suppliers || []).includes(s.id));

  // Find best price per item
  const bestPrices = {};
  (map.items || []).forEach(item => {
    let best = Infinity;
    mapSuppliers.forEach(s => {
      const p = getQuote(s.id, item.id);
      if (p !== undefined && p < best) best = p;
    });
    if (best < Infinity) bestPrices[item.id] = best;
  });

  // Supplier totals
  const supplierTotals = {};
  mapSuppliers.forEach(s => {
    supplierTotals[s.id] = (map.items || []).reduce((sum, item) => {
      const p = getQuote(s.id, item.id);
      return sum + (p ? p * item.quantity : 0);
    }, 0);
  });

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <button onClick={onBack} className="text-xs font-medium cursor-pointer mb-1" style={{ color: 'var(--text-light)', background: 'none', border: 'none' }}>&#8592; Voltar</button>
      <h1 className="text-xl font-bold mb-1">{map.name}</h1>
      <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>{map.description}</p>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select data-testid="map-add-supplier" className="select" style={{ width: 'auto' }} onChange={e => { if (e.target.value) addSupplierToMap(e.target.value); e.target.value = ''; }}>
          <option value="">+ Adicionar fornecedor...</option>
          {availableSuppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button onClick={() => setShowImportBudget(true)} className="btn btn-outline btn-sm">
          <Package size={14} /> Importar do Orcamento
        </button>
        <button onClick={() => setShowImportPdf(true)} className="btn btn-outline btn-sm">
          <FileUp size={14} /> Importar PDF Fornecedor
        </button>
      </div>

      {/* Import from Budget Modal */}
      {showImportBudget && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="pro-card-padded w-full max-w-md animate-in">
            <h2 className="text-lg font-bold mb-3">Importar Materiais do Orcamento</h2>
            <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
              Selecione um orcamento para importar os materiais como itens do mapa comparativo.
            </p>
            {budgets.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                {budgets.map(b => (
                  <div 
                    key={b.id} 
                    className="p-3 rounded-md cursor-pointer hover:shadow-md transition-shadow"
                    style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}
                    onClick={() => importFromBudget(b.id)}
                  >
                    <p className="text-sm font-semibold">{b.description || 'Orcamento ' + b.id.substring(0, 8)}</p>
                    <p className="text-xs" style={{ color: 'var(--text-light)' }}>
                      {b.client_name} - {b.items?.length || 0} itens - {b.total?.toLocaleString('pt-PT')} EUR
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs py-4 text-center" style={{ color: 'var(--text-light)' }}>Nenhum orcamento disponivel</p>
            )}
            <button onClick={() => setShowImportBudget(false)} className="btn btn-outline w-full">Fechar</button>
          </div>
        </div>
      )}

      {/* Import PDF Modal */}
      {showImportPdf && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="pro-card-padded w-full max-w-lg animate-in">
            <h2 className="text-lg font-bold mb-3">Importar PDF do Fornecedor</h2>
            <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
              Carregue um PDF de cotacao do fornecedor. O sistema tentara extrair os precos automaticamente.
            </p>
            
            <div className="mb-3">
              <label className="field-label">Fornecedor</label>
              <select 
                className="select w-full" 
                value={selectedSupplierForPdf}
                onChange={e => setSelectedSupplierForPdf(e.target.value)}
              >
                <option value="">Selecionar fornecedor...</option>
                {mapSuppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            
            {selectedSupplierForPdf && (
              <div className="mb-4">
                <label className="field-label">Ficheiro PDF</label>
                <label className="flex items-center justify-center gap-2 p-6 rounded-md border-2 border-dashed cursor-pointer hover:border-blue-400 transition-colors" style={{ borderColor: 'var(--border)' }}>
                  {uploadingPdf ? (
                    <span className="text-sm">A processar...</span>
                  ) : (
                    <>
                      <Upload size={20} style={{ color: 'var(--text-light)' }} />
                      <span className="text-sm">Clique para carregar PDF</span>
                    </>
                  )}
                  <input type="file" accept=".pdf" className="hidden" onChange={handlePdfUpload} disabled={uploadingPdf} />
                </label>
              </div>
            )}
            
            {/* Show extracted items */}
            {pdfItems.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold mb-2">Itens Extraidos ({pdfItems.length})</h3>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {pdfItems.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-xs p-2 rounded" style={{ background: 'var(--surface-alt)' }}>
                      <span className="truncate flex-1">{item.description}</span>
                      <span className="font-semibold ml-2">{item.price?.toLocaleString('pt-PT')} EUR</span>
                    </div>
                  ))}
                </div>
                <button onClick={applyPdfPrices} className="btn btn-primary w-full mt-3">
                  <CheckCircle size={14} /> Aplicar Precos aos Itens
                </button>
              </div>
            )}
            
            <button onClick={() => { setShowImportPdf(false); setPdfItems([]); setSelectedSupplierForPdf(''); }} className="btn btn-outline w-full">Fechar</button>
          </div>
        </div>
      )}

      {/* Add item manually */}
      <div className="flex gap-2 mb-4">
        <input className="input flex-1" placeholder="Descricao do item" value={itemDesc} onChange={e => setItemDesc(e.target.value)} />
        <input className="input" style={{ width: '80px' }} placeholder="Un." value={itemUnit} onChange={e => setItemUnit(e.target.value)} />
        <input className="input" style={{ width: '80px' }} type="number" placeholder="Qtd" value={itemQty} onChange={e => setItemQty(parseFloat(e.target.value) || 1)} />
        <button data-testid="map-add-item" onClick={addItem} className="btn btn-primary btn-sm"><Plus size={14} /> Item</button>
      </div>

      {/* Comparison table */}
      {(map.items || []).length > 0 && mapSuppliers.length > 0 ? (
        <div className="pro-card overflow-hidden" data-testid="comparison-table">
          <div className="overflow-x-auto">
            <table className="pro-table">
              <thead>
                <tr>
                  <th>Item</th><th>Qtd</th>
                  {mapSuppliers.map(s => (
                    <th key={s.id}>
                      <div className="flex items-center gap-1">
                        <span className="truncate max-w-[100px]">{s.name}</span>
                        <button onClick={() => removeSupplierFromMap(s.id)} className="text-red-400 hover:text-red-600"><X size={10} /></button>
                      </div>
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(map.items || []).map(item => (
                  <tr key={item.id}>
                    <td className="text-xs font-medium">{item.description}</td>
                    <td className="text-xs">{item.quantity} {item.unit}</td>
                    {mapSuppliers.map(s => {
                      const price = getQuote(s.id, item.id);
                      const isBest = bestPrices[item.id] !== undefined && price === bestPrices[item.id];
                      return (
                        <td key={s.id} style={{ background: isBest ? '#E8F5EC' : 'transparent' }}>
                          <input className="input text-xs text-right" type="number" step="0.01" placeholder="0.00" value={price || ''} onChange={e => addQuote(s.id, item.id, parseFloat(e.target.value) || 0)} style={{ width: '90px', padding: '4px 6px', background: isBest ? '#E8F5EC' : undefined }} />
                          {isBest && <CheckCircle size={10} className="inline ml-1 text-green-600" />}
                        </td>
                      );
                    })}
                    <td><button onClick={() => removeItem(item.id)} className="btn btn-danger btn-xs"><Trash2 size={10} /></button></td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr style={{ background: '#f1f5f9', fontWeight: 'bold' }}>
                  <td colSpan={2} className="text-xs font-bold">TOTAL</td>
                  {mapSuppliers.map(s => {
                    const total = supplierTotals[s.id] || 0;
                    const allTotals = Object.values(supplierTotals).filter(t => t > 0);
                    const isBest = allTotals.length > 0 && total === Math.min(...allTotals) && total > 0;
                    return (
                      <td key={s.id} className="text-xs" style={{ background: isBest ? '#d1fae5' : undefined, color: isBest ? '#047857' : undefined }}>
                        <span className="font-bold">{total.toLocaleString('pt-PT')} EUR</span>
                        {isBest && <CheckCircle size={10} className="inline ml-1" />}
                      </td>
                    );
                  })}
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="pro-card-padded text-center py-8">
          <FileText size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} />
          <p className="font-semibold">Adicione itens e fornecedores</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Crie itens para comparar e adicione fornecedores para inserir precos</p>
        </div>
      )}
    </div>
  );
}

export default function ComparisonMapsPage() {
  const [maps, setMaps] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });

  const load = useCallback(() => {
    api.get('/comparison-maps').then(r => setMaps(r.data)).catch(() => {});
    api.get('/suppliers').then(r => setSuppliers(r.data)).catch(() => {});
    api.get('/budgets').then(r => setBudgets(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name) return;
    const { data } = await api.post('/comparison-maps', form);
    setCreating(false); setForm({ name: '', description: '' });
    setSelected(data); load();
  };

  const handleDelete = async (id) => { if (window.confirm('Eliminar?')) { await api.delete(`/comparison-maps/${id}`); load(); } };

  if (selected) return <ComparisonMapDetail map={selected} onBack={() => { setSelected(null); load(); }} suppliers={suppliers} budgets={budgets} />;

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold">Mapas Comparativos</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Compare precos entre fornecedores</p>
        </div>
        <button data-testid="new-map-btn" onClick={() => setCreating(true)} className="btn btn-primary"><Plus size={16} /> Novo Mapa</button>
      </div>

      {creating && (
        <div className="pro-card-padded mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input data-testid="map-name" className="input" placeholder="Nome do mapa" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            <input className="input" placeholder="Descricao" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            <div className="flex gap-2">
              <button data-testid="create-map-btn" onClick={create} className="btn btn-primary flex-1">Criar</button>
              <button onClick={() => setCreating(false)} className="btn btn-outline">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {maps.length === 0 ? (
        <div className="pro-card-padded text-center py-8"><Euro size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} /><p className="font-semibold">Sem mapas comparativos</p><p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Crie um mapa para comparar precos de fornecedores</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {maps.map(m => (
            <div key={m.id} data-testid={`map-card-${m.id}`} className="pro-card-padded cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelected(m)}>
              <h3 className="text-sm font-bold">{m.name}</h3>
              <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>{m.description || 'Sem descricao'}</p>
              <div className="flex gap-2 text-[10px]" style={{ color: 'var(--text-light)' }}>
                <span>{(m.items || []).length} itens</span>
                <span>{(m.suppliers || []).length} fornecedores</span>
                <span>{(m.quotes || []).length} cotacoes</span>
              </div>
              <div className="flex gap-1 mt-2">
                <button onClick={e => { e.stopPropagation(); handleDelete(m.id); }} className="btn btn-danger btn-xs"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
